/**
 * The one place that writes SEO metadata into the document.
 *
 * Before this, four things wrote to `document.title` and `meta[name=description]`
 * — `index.html`, App's initialization effect, `FaviconManager`, and each page's
 * `usePageSeo` — and which value survived depended on effect ordering. A whole
 * module (`routeSeo.ts`) existed only to keep them from overwriting each other.
 *
 * `applySeo` replaces every managed tag on every call, so the document always
 * reflects the current page exactly: no restore-on-unmount, no leftovers from
 * the page before. Managed tags carry `data-seo` so they can be cleared without
 * touching the fonts, favicon or analytics tags that share `<head>`.
 */
import {
  DEFAULT_OG_IMAGE,
  DEFAULT_OG_IMAGE_HEIGHT,
  DEFAULT_OG_IMAGE_WIDTH,
  breadcrumbTrail,
  type PageSeo,
} from '../config/seo';
import { SITE_URL } from '../config/site';
import { organizationJsonLd, webSiteJsonLd } from '../config/structuredData';

// Re-exported so existing imports of the builders from here keep working.
export { organizationJsonLd, webSiteJsonLd };

export interface SeoInput extends PageSeo {
  /** Route path, not a full URL. Canonical and breadcrumbs are built from it. */
  path: string;
  /** Extra JSON-LD for this page, e.g. the BlogPosting for an article. */
  jsonLd?: Record<string, unknown>[];
  /** A staging build asks robots to stay away from every page. */
  forceNoindex?: boolean;
}

const MANAGED_ATTRIBUTE = 'data-seo';

function upsertMeta(
  selector: string,
  attribute: 'name' | 'property',
  key: string,
  content: string
) {
  let tag = document.head.querySelector<HTMLMetaElement>(selector);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, key);
    tag.setAttribute(MANAGED_ATTRIBUTE, '');
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function setName(name: string, content: string) {
  upsertMeta(`meta[name="${name}"]`, 'name', name, content);
}

function setProperty(property: string, content: string) {
  upsertMeta(`meta[property="${property}"]`, 'property', property, content);
}

/** For a page that must not declare one; see `applySeo`. */
function removeManaged(selector: string) {
  document.head.querySelectorAll(selector).forEach((node) => node.remove());
}

function setCanonical(url: string) {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    link.setAttribute(MANAGED_ATTRIBUTE, '');
    document.head.appendChild(link);
  }
  link.href = url;
}

/**
 * Structured data is replaced wholesale rather than updated in place: the number
 * of blocks differs per page (the home page adds Organization and WebSite, an
 * article adds BlogPosting), and a stale block left behind describes the wrong
 * page to every consumer that reads it.
 */
function setJsonLd(blocks: Record<string, unknown>[]) {
  document.head
    .querySelectorAll(`script[type="application/ld+json"][${MANAGED_ATTRIBUTE}]`)
    .forEach((node) => node.remove());

  for (const block of blocks) {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute(MANAGED_ATTRIBUTE, '');
    script.textContent = JSON.stringify(block);
    document.head.appendChild(script);
  }
}

/**
 * Only for pages that have at least one real ancestor. A single-item trail is
 * just the page itself, which tells a search engine nothing it does not already
 * know from the URL.
 */
export function breadcrumbJsonLd(
  path: string,
  currentName?: string
): Record<string, unknown> | undefined {
  const trail = breadcrumbTrail(path);
  /*
   * A page outside the registry -- a blog article -- has ancestors there but is
   * not in it itself, so the trail used to stop at "Blog". The page it is on
   * belongs at the end.
   */
  if (currentName && trail[trail.length - 1]?.path !== path) {
    trail.push({ path, name: currentName });
  }
  if (trail.length < 2) return undefined;

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: trail.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: `${SITE_URL}${crumb.path === '/' ? '/' : crumb.path}`,
    })),
  };
}

export function canonicalUrl(path: string): string {
  return path === '/' ? `${SITE_URL}/` : `${SITE_URL}${path}`;
}

export function applySeo(input: SeoInput): void {
  const {
    path,
    title,
    description,
    ogImage = DEFAULT_OG_IMAGE,
    ogType = 'website',
    jsonLd = [],
    forceNoindex = false,
  } = input;

  const canonical = canonicalUrl(path);
  const robots = forceNoindex ? 'noindex, nofollow' : (input.robots ?? 'index, follow');

  /*
   * A page that is itself noindex declares no canonical and no og:url. The two
   * contradict each other -- one says "this is the URL to index", the other
   * "do not index this" -- and Google advises against the pair. It also stopped
   * the 404 page publishing the prerender probe's address,
   * /__prerender-not-found, as its canonical.
   *
   * Keyed on the page's own robots, not on `forceNoindex`: staging is noindex
   * everywhere but keeps its canonicals, which point at production, so that a
   * staging page found anyway still hands its signals to the live one.
   */
  const pageIsNoindex = (input.robots ?? '').includes('noindex');

  document.title = title;
  setName('description', description);
  setName('robots', robots);
  if (pageIsNoindex) {
    removeManaged('link[rel="canonical"]');
    removeManaged('meta[property="og:url"]');
    removeManaged('meta[name="twitter:url"]');
  } else {
    setCanonical(canonical);
  }

  setProperty('og:type', ogType);
  setProperty('og:site_name', 'WeAreQA');
  // The site is English-only; <html lang> says so, and this is where a social
  // card generator looks for the same fact.
  setProperty('og:locale', 'en_US');
  if (!pageIsNoindex) setProperty('og:url', canonical);
  setProperty('og:title', title);
  setProperty('og:description', description);
  setProperty('og:image', ogImage);
  if (ogImage === DEFAULT_OG_IMAGE) {
    setProperty('og:image:width', String(DEFAULT_OG_IMAGE_WIDTH));
    setProperty('og:image:height', String(DEFAULT_OG_IMAGE_HEIGHT));
  } else {
    // The dimensions describe the default card only; left in place they would
    // claim an article's own image is 1200x800 too.
    removeManaged('meta[property="og:image:width"]');
    removeManaged('meta[property="og:image:height"]');
  }

  setName('twitter:card', 'summary_large_image');
  if (!pageIsNoindex) setName('twitter:url', canonical);
  setName('twitter:title', title);
  setName('twitter:description', description);
  setName('twitter:image', ogImage);

  /*
   * A noindex page contributes nothing to structured data, and marking up a 404
   * as part of a breadcrumb trail is actively wrong.
   */
  const blocks = robots.includes('noindex')
    ? []
    : [...(path === '/' ? [organizationJsonLd(), webSiteJsonLd()] : []), ...jsonLd];

  const breadcrumbs = robots.includes('noindex')
    ? undefined
    : breadcrumbJsonLd(path, input.breadcrumb);
  if (breadcrumbs) blocks.push(breadcrumbs);

  setJsonLd(blocks);
}
